import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { downloadJson, formatDateTime, useOmniResource } from "./omni";

// Its own module, not App.js (3000+ lines) and deliberately not omni.js: omni.js is the INBOX
// module, and putting a voice-only tenant's compliance UI there would repeat the very conflation
// that caused the gap this page closes — those tenants have no inbox, so they had no contacts and
// no way to answer "delete everything about me". Import direction: App.js -> datenschutz.js -> omni.js.

// Word-for-word the note of the disabled transcript row in App.js, because the Vorschau has to teach
// ONE rule: nothing in it acts. Rendered next to every control `previewMode` kills.
const PREVIEW_NOTE = <span className="text-xs text-muted-foreground" data-testid="preview-disabled-note">In der Vorschau deaktiviert</span>;

// Moved out of SettingsPage so both places can render it: the card is live on a production tenant,
// so dropping it from Einstellungen would be a regression.
export function ClientExportCard({ api, clientId, previewMode = false }) {
  const [exporting, setExporting] = useState(false);

  // Art. 15/20 self-service export. Not admin-only: this is the client's own data.
  const exportClientData = async () => {
    setExporting(true);
    try {
      downloadJson(await api(`/clients/${clientId}/export`), `meine-daten-${clientId}.json`);
      toast.success("Export heruntergeladen");
    } catch (err) { toast.error(err.message); } finally { setExporting(false); }
  };

  return (
    <Card className="dashboard-card" data-testid="gdpr-export-card">
      <CardHeader><CardTitle>Datenschutz — meine Daten</CardTitle><CardDescription>Auskunft und Datenübertragbarkeit nach Art. 15 und Art. 20 DSGVO.</CardDescription></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Lädt alle Gespräche mit Transkripten, Kontakte und eine Nutzungsübersicht als JSON-Datei herunter —
          inklusive der Informationen zur Verarbeitung nach Art. 15 DSGVO. Bereits gelöschte Gespräche sind nicht enthalten.
        </p>
        <div className="flex gap-2 flex-wrap items-center">
          <Button variant="outline" onClick={exportClientData} disabled={exporting || previewMode} data-testid="client-export-button">
            {exporting ? "Export läuft…" : "Meine Daten exportieren (JSON)"}
          </Button>
          {previewMode ? PREVIEW_NOTE : null}
        </div>
      </CardContent>
    </Card>
  );
}

// The eight actions GET /clients/{id}/gdpr-log may return. Raw action keys are internal vocabulary
// and are never shown to a client -- an unmapped key falls back to a generic German label rather
// than to "data_subject.erased". Mirror of GDPR_LOG_ACTIONS in server.py; change both together.
const GDPR_LOG_LABELS = {
  "client.exported": "Konto-Export (Art. 15)",
  "contact.exported": "Auskunft zu einem Kontakt (Art. 15)",
  "contact.erased": "Löschung eines Kontakts (Art. 17)",
  "conversation.exported": "Export eines Gesprächs (Art. 15)",
  "conversation.erased": "Löschung eines Gesprächs (Art. 17)",
  "data_subject.searched": "Suche nach einer betroffenen Person",
  "data_subject.exported": "Auskunft zu einer betroffenen Person (Art. 15)",
  "data_subject.erased": "Löschung einer betroffenen Person (Art. 17)",
};

// The coverage start is rendered WITH its time of day, in UTC. The server derives it from a marker
// stamped mid-day at deploy, so a bare day („ab dem 30.7.2026") would claim every erase the old code
// still performed that morning — unattributable, missing from the panel, and yet inside the claimed
// day. Exactly the „fourteen hours that do not exist" the backend refuses to claim
// (`_gdpr_log_coverage` in server.py) and the „unterschätzen, niemals überschätzen" of
// docs/legal/loeschkonzept.md § L6, so truncating it here would defeat both at the last step.
// timeZone UTC because `from` is the stored UTC moment (and, on a legacy response, a date-only
// string, which Date parses as UTC midnight): without it a browser west of UTC renders the PREVIOUS
// day and the panel claims a coverage it does not have.
const COVERAGE_MOMENT_FORMAT = { timeZone: "UTC", year: "numeric", month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" };

// Art. 5 Abs. 2: a self-service erase button without a record of who pressed it is not a compliance
// surface. Read-only, so it hangs off the same `export` right as the other reads.
function GdprLogCard({ api, clientId }) {
  const log = useOmniResource(api, `/clients/${clientId}/gdpr-log`, [clientId]);
  const entries = log.data?.entries || [];
  // Only the actions whose history is genuinely incomplete carry a date. A single global "covers
  // from" would claim a gap for contact.* and client.exported as well, which were tenant-filterable
  // from the start — a panel that overstates its own blind spot is as misleading as one that hides it.
  const gaps = Object.entries(log.data?.covers_from || {}).filter(([, from]) => from);
  // The third case: the server could not date the start of completeness at all (no first-boot marker
  // and no attributable entry yet). `null` in covers_from means „von Anfang an vollständig" for the
  // other seven actions, so this must be its own sentence — never a date and never silence.
  const unknown = log.data?.covers_from_unknown || [];

  return (
    <Card className="dashboard-card" data-testid="gdpr-log-card">
      <CardHeader>
        <CardTitle>Protokoll</CardTitle>
        <CardDescription>Nachweis nach Art. 5 Abs. 2 DSGVO — welche Auskünfte und Löschungen in diesem Konto ausgeführt wurden.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {log.loading ? <Skeleton className="h-32" data-testid="gdpr-log-loading" /> : null}
        {log.error ? <p className="text-sm text-destructive">{log.error}</p> : null}
        {!log.loading && !log.error && !entries.length ? (
          <p className="text-sm text-muted-foreground" data-testid="gdpr-log-empty">
            Bisher wurden keine Auskünfte oder Löschungen ausgeführt.
          </p>
        ) : null}
        {entries.length ? (
          <Table data-testid="gdpr-log-table">
            <TableHeader><TableRow><TableHead>Datum</TableHead><TableHead>Aktion</TableHead><TableHead>handelnde Person</TableHead><TableHead>Objekt</TableHead></TableRow></TableHeader>
            <TableBody>
              {entries.map((row, index) => (
                <TableRow key={`${row.created_at}-${row.target}-${index}`} data-testid="gdpr-log-row">
                  <TableCell>{formatDateTime(row.created_at)}</TableCell>
                  <TableCell>{GDPR_LOG_LABELS[row.action] || "Sonstige DSGVO-Aktion"}</TableCell>
                  <TableCell>{row.actor_email || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{row.target || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
        {gaps.length ? (
          <p className="text-xs text-muted-foreground" data-testid="gdpr-log-covers-from">
            Hinweis: {gaps.map(([action, from]) => `„${GDPR_LOG_LABELS[action] || "Sonstige DSGVO-Aktion"}“ ist erst ab dem ${new Date(from).toLocaleString("de-DE", COVERAGE_MOMENT_FORMAT)} Uhr (UTC) vollständig protokolliert`).join("; ")}
            {" "}— frühere Einträge dieser Art lassen sich keinem Konto zuordnen und fehlen hier. Alle
            {" "}übrigen Aktionen sind von Anfang an enthalten.
          </p>
        ) : null}
        {unknown.length ? (
          <p className="text-xs text-muted-foreground" data-testid="gdpr-log-covers-from-unknown">
            Hinweis: Für {unknown.map((action) => `„${GDPR_LOG_LABELS[action] || "Sonstige DSGVO-Aktion"}“`).join("; ")}
            {" "}ist nicht bekannt, ab wann die Protokollierung vollständig ist; es ist hier bislang kein
            {" "}Eintrag dieser Art als vollständig erfasst — frühere Einträge lassen sich keinem Konto
            {" "}zuordnen und fehlen. Alle übrigen Aktionen sind von Anfang an enthalten.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

// `previewMode` is set only by the admin Vorschau (App.js): the page renders in full so the operator
// sees what a member gets, but every control that would CALL the backend is dead. The Vorschau is a
// screen that promises to show, not to act — and here „acting" means writing a row into the tenant's
// client-visible Art.-5(2) Protokoll, downloading real patient data, or erasing a person at
// ElevenLabs irreversibly. The sibling transcript row is disabled for the same reason, and two
// neighbouring tabs must not teach opposite rules. GdprLogCard stays live: it is a GET, it logs
// nothing, and it is the one panel the operator came to read.
export function DatenschutzPage({ api, clientId, canGdpr = {}, previewMode = false }) {
  const navigate = useNavigate();
  const [queryInput, setQueryInput] = useState("");
  const [subject, setSubject] = useState("");        // the SUBMITTED identifier, never the live input
  const [result, setResult] = useState(null);        // { subject, data } — stamped with what it describes
  const [searching, setSearching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [erasePreview, setErasePreview] = useState(null);  // { subject, ...would_delete }
  const [previewing, setPreviewing] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [confirmValue, setConfirmValue] = useState("");

  // Same guard as ContactsPage (omni.js): whenever the subject changes, any preview or typed
  // confirmation from the previous one is dropped. Without this, an in-flight dry-run that resolves
  // after a new search could arm the confirm panel over a DIFFERENT person — showing A's counts
  // above a button that erases B.
  useEffect(() => { setErasePreview(null); setConfirmValue(""); }, [subject]);

  // Explicit submit, no debounce: with equality selection incremental search is pointless, and
  // every keystroke would write an audit row into a log that has no retention bound.
  const runSearch = async (q) => {
    setSearching(true);
    try {
      const data = await api(`/clients/${clientId}/data-subject/search`, { method: "POST", body: JSON.stringify({ q }) });
      setResult({ subject: q, data });   // rendered only while it still describes the open subject
    } catch (err) {
      setResult(null);
      toast.error(err.message);
    } finally { setSearching(false); }
  };

  const submitSearch = (event) => {
    event.preventDefault();
    // Not only the button: pressing Enter in the input is an implicit form submission, and whether a
    // disabled submit button suppresses it is browser-dependent. A search is an audit row.
    if (previewMode) return;
    const q = queryInput.trim();
    if (!q) return;
    setSubject(q);
    runSearch(q);
  };

  const exportSubject = async () => {
    const target = subject;
    setExporting(true);
    try {
      const data = await api(`/clients/${clientId}/data-subject/export`, { method: "POST", body: JSON.stringify({ q: target }) });
      // NEVER the number in the filename — it would otherwise sit in the operator's Downloads
      // folder. The colons of an ISO timestamp are not valid in a Windows filename, hence the dashes.
      downloadJson(data, `betroffene-person-${new Date().toISOString().replace(/[:.]/g, "-")}.json`);
      toast.success("Export heruntergeladen");
    } catch (err) { toast.error(err.message); } finally { setExporting(false); }
  };

  // Step 1 of the two-step erase: dry_run=true is a pure preview, it deletes nothing. The subject is
  // captured up front and stamped onto the preview, so a slow response can never paint one person's
  // deletion counts onto another person's confirm panel.
  // `previewing` is not cosmetic: without it the destructive button accepted repeated clicks and
  // fired a dry-run per click, each of which counts the same person's data again.
  const previewErase = async () => {
    const target = subject;
    setConfirmValue("");
    setPreviewing(true);
    try {
      const preview = await api(`/clients/${clientId}/data-subject?dry_run=true`, {
        method: "DELETE", body: JSON.stringify({ q: target }),
      });
      setErasePreview({ ...preview.would_delete, subject: target });
    } catch (err) { toast.error(err.message); } finally { setPreviewing(false); }
  };

  // Step 2: the real, irreversible run — aimed at the subject the preview was computed for, never
  // at whatever is in the input field now.
  const confirmErase = async () => {
    const target = erasePreview?.subject;
    if (!target) return;
    setErasing(true);
    try {
      const outcome = await api(`/clients/${clientId}/data-subject?dry_run=false&delete_provider=true`, {
        method: "DELETE", body: JSON.stringify({ q: target, confirm: confirmValue }),
      });
      if (outcome.status === "complete") toast.success("Gespräche der betroffenen Person gelöscht");
      else if (outcome.status === "nothing_to_do") toast.info("Zu dieser Kennung war nichts mehr zu löschen.");
      else if (outcome.status === "incomplete") toast.error(outcome.failures?.length
        ? `Löschung unvollständig — ${outcome.failures.length} Gespräch(e) konnten nicht gelöscht werden. Bitte erneut versuchen.`
        : `Während der Löschung sind ${outcome.arrived_conversation_ids?.length || 0} neue Gespräch(e) dieser Person eingegangen. Bitte erneut löschen.`);
      else if (outcome.status === "local_only") toast.warning(`Lokal gelöscht — bei ${outcome.skipped_at_provider?.length || 0} Gespräch(en) konnten die Daten beim Anbieter nicht gelöscht werden; dort ist separat zu löschen.`);
      else toast.warning(`Lokal gelöscht — ${outcome.failures?.length || 0} Löschung(en) beim Anbieter noch nicht bestätigt.`);
      setErasePreview(null);
      setConfirmValue("");
      await runSearch(target);  // show what is left for the SAME subject
    } catch (err) { toast.error(err.message); } finally { setErasing(false); }
  };

  const found = result && result.subject === subject ? result.data : null;

  return (
    <div className="space-y-5" data-testid="datenschutz-page">
      {canGdpr.export ? <ClientExportCard api={api} clientId={clientId} previewMode={previewMode} /> : null}

      {canGdpr.export || canGdpr.erase ? (
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle>Auskunft zu einer betroffenen Person</CardTitle>
            <CardDescription>Rufnummer oder E-Mail-Adresse eingeben — gesucht wird exakt, nicht nach Teilstücken.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form className="filters-bar" onSubmit={submitSearch}>
              {/* A clearly fictional example number (all-zero Austrian mobile) -- the placeholder is
                  shown to a client, so it must not be somebody's real line. */}
              <Input className="bg-white" placeholder="+43 660 0000000 oder name@example.com"
                data-testid="datenschutz-search-input" value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)} />
              <Button type="submit" disabled={previewMode || searching || !queryInput.trim()} data-testid="datenschutz-search-button">
                {searching ? "Suche läuft…" : "Suchen"}
              </Button>
              {previewMode ? PREVIEW_NOTE : null}
            </form>

            {searching && !found ? <Skeleton className="h-40" data-testid="state-loading" /> : null}

            {/* Everything below exists only once a search has been SUBMITTED — before that there is
                nothing to act on. The result block and the erase controls are separate on purpose:
                the search needs the Auskunft right, an Art. 17 erasure does not (and must not be
                blocked by the conversations display toggle either — same asymmetry as the server). */}
            {subject ? (
              <div className="space-y-3" data-testid="datenschutz-result">
                {found ? (<>
                <p className="text-sm">
                  <strong>{found.conversation_count}</strong> Gespräch(e) zu dieser Kennung
                  {found.conversation_count ? <> — {formatDateTime(found.first_conversation_at)} bis {formatDateTime(found.last_conversation_at)}</> : null}
                  {found.contact_matched ? " (Kontaktdatensatz vorhanden)" : null}
                </p>
                {found.rows_truncated ? (
                  <p className="text-xs text-muted-foreground">Es werden nur die neuesten {found.rows?.length} Gespräche angezeigt; Export und Löschung umfassen alle.</p>
                ) : null}
                {found.rows?.length ? (
                  <Table data-testid="datenschutz-result-table">
                    <TableHeader><TableRow><TableHead>Zeitpunkt</TableHead><TableHead>Agent</TableHead><TableHead>Dauer</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {found.rows.map((row) => (
                        <TableRow key={row.id} data-testid="datenschutz-result-row" className="cursor-pointer" role="button" tabIndex={0}
                          onClick={() => navigate(`/clients/${clientId}/conversations/${row.id}`)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/clients/${clientId}/conversations/${row.id}`); } }}>
                          <TableCell>{formatDateTime(row.started_at)}</TableCell>
                          <TableCell>{row.agent_name || "—"}</TableCell>
                          <TableCell>{row.duration_seconds != null ? `${row.duration_seconds} s` : "—"}</TableCell>
                          <TableCell>{row.status || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : <p className="text-sm text-muted-foreground" data-testid="datenschutz-empty">Zu dieser Kennung sind keine Gespräche gespeichert.</p>}

                {/* Stated openly rather than hidden: a search by number can never reach these — and
                    the first set is NOT only web/test sessions, a suppressed number lands there too. */}
                <p className="text-xs text-muted-foreground" data-testid="datenschutz-without-caller">
                  Hinweis: {found.conversations_without_caller_id} Gespräch(e) ohne gespeicherte Anrufer-Kennung
                  {" "}(nicht per Telefon geführte Gespräche sowie Anrufe mit unterdrückter Rufnummer) sind über diese
                  {" "}Suche nicht auffindbar
                  {found.conversations_with_national_format_number
                    ? <>; ebenso {found.conversations_with_national_format_number} Gespräch(e), deren Rufnummer in
                        {" "}nationaler Schreibweise (führende 0) gespeichert ist — sie ist über keine zulässige
                        {" "}Eingabe erreichbar</>
                    : null}.
                </p>

                {/* Said BEFORE the operator starts: the erase refuses above the cap, so inviting an
                    Art.-17 request that always fails at the confirm step would be the wrong order. */}
                {canGdpr.erase && found.erase_conversation_cap != null
                  && found.conversation_count > found.erase_conversation_cap ? (
                  <p className="text-xs text-destructive" data-testid="datenschutz-erase-cap">
                    Löschung über diese Seite ist auf {found.erase_conversation_cap} Gespräche begrenzt; bei
                    {" "}{found.conversation_count} Gespräch(en) ist der Support zu kontaktieren, damit die Löschung
                    {" "}in Teilschritten ausgeführt wird.
                  </p>
                ) : null}
                </>) : null}

                {/* `previewMode` on both is belt and braces: this whole block hangs off a submitted
                    `subject`, which the Vorschau can never produce because the search is dead. The
                    guard sits on the buttons themselves so a later change to that gate cannot make an
                    erase live again — and note the erase's pending label („Vorschau läuft…") means
                    the dry-run, not this screen. */}
                <div className="flex gap-2 flex-wrap items-center">
                  {canGdpr.export && found ? (
                    <Button variant="outline" size="sm" onClick={exportSubject} disabled={exporting || previewMode} data-testid="datenschutz-export-button">
                      {exporting ? "Export läuft…" : "Auskunft exportieren (JSON)"}
                    </Button>
                  ) : null}
                  {canGdpr.erase ? (
                    <Button variant="destructive" size="sm" onClick={previewErase} disabled={previewing || previewMode} data-testid="datenschutz-erase-button">
                      {previewing ? "Vorschau läuft…" : "DSGVO löschen"}
                    </Button>
                  ) : null}
                  {previewMode ? PREVIEW_NOTE : null}
                </div>

                {erasePreview?.subject === subject ? (
                  <div className="rounded-md border border-destructive/40 p-3 space-y-2 text-sm" data-testid="datenschutz-erase-confirm">
                    <p className="font-medium text-destructive">Alle Daten dieser Person endgültig löschen — nicht rückgängig zu machen.</p>
                    {/* The two contact outcomes are separate because the run treats them separately:
                        the searched identifier is always removed from the contact, but the record
                        itself is only anonymized when no other conversation still points at it.
                        „sowie der Kontaktdatensatz" for both promised more than the run delivers. */}
                    <p className="text-muted-foreground">
                      Gelöscht werden: {erasePreview.conversations} Gespräch(e), {erasePreview.messages} Nachricht(en),
                      {" "}{erasePreview.workflow_runs} Workflow-Lauf/-Läufe, {erasePreview.webhook_events} Rohdatensatz/-sätze
                      {erasePreview.contact_anonymized
                        ? " sowie der Kontaktdatensatz, der anonymisiert wird"
                        : erasePreview.contact_updated
                          ? "; die gesuchte Kennung wird aus dem Kontaktdatensatz entfernt, der Datensatz selbst bleibt bestehen (weitere Gespräche verweisen darauf)"
                          : ""}. Aufzeichnungen beim Sprachanbieter
                      werden ebenfalls gelöscht; Abrechnungsdaten bleiben aus steuerrechtlichen Gründen erhalten.
                    </p>
                    <p className="text-muted-foreground">Zum Bestätigen die gesuchte Rufnummer bzw. E-Mail-Adresse eingeben:</p>
                    <Input className="bg-white" value={confirmValue} onChange={(e) => setConfirmValue(e.target.value)}
                      placeholder="+43… oder name@example.com" data-testid="datenschutz-erase-confirm-input" />
                    <div className="flex gap-2">
                      {/* `previewMode` here too, like its two siblings above: this panel needs the
                          dead search and the dead dry-run revived to be reachable at all, so the
                          guard is defence in depth — but on an irreversible provider-side erase every
                          control has to be safe on its own, not because an upstream one is dead. */}
                      <Button variant="destructive" size="sm" disabled={erasing || previewMode || !confirmValue.trim()} onClick={confirmErase} data-testid="datenschutz-erase-submit-button">
                        {erasing ? "Löschen läuft…" : "Endgültig löschen"}
                      </Button>
                      <Button variant="outline" size="sm" disabled={erasing} onClick={() => setErasePreview(null)}>Abbrechen</Button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Static text, no export call just to render it. */}
            <details className="text-sm" data-testid="datenschutz-processing-info">
              <summary className="cursor-pointer font-medium">Informationen zur Verarbeitung (Art. 15)</summary>
              <div className="mt-2 space-y-2 text-muted-foreground">
                <p>
                  Verantwortlicher für die hier gespeicherten Daten ist dieses Kundenkonto; der Betreiber der Konsole
                  verarbeitet sie ausschließlich weisungsgebunden nach Art. 28 DSGVO. Gespeichert werden Kontaktdaten
                  (Rufnummer, E-Mail-Adresse, Name, soweit angegeben), Inhaltsdaten (Transkripte, Zusammenfassungen,
                  erkannte Themen) sowie Metadaten (Zeitpunkt, Dauer, Kanal).
                </p>
                <p>
                  Gespräche ohne gespeicherte Anrufer-Kennung (nicht per Telefon geführte Gespräche sowie Anrufe mit
                  unterdrückter Rufnummer) sind über diese Suche nicht auffindbar; das gilt ebenso für Gespräche, deren
                  Rufnummer in nationaler Schreibweise (führende 0) gespeichert ist. Der JSON-Export enthält den
                  vollständigen Art.-15-Informationsblock inklusive Empfängern und Speicherdauern.
                </p>
                <p>
                  Abrechnungsdaten (Anzahl Gespräche und Minuten) bleiben wegen der handels- und steuerrechtlichen
                  Aufbewahrungspflichten auch nach einer Löschung erhalten; sie enthalten keine Gesprächsinhalte.
                </p>
              </div>
            </details>
          </CardContent>
        </Card>
      ) : null}

      {canGdpr.export ? <GdprLogCard api={api} clientId={clientId} /> : null}
    </div>
  );
}
